import React, { useEffect, useState } from 'react';
import { Sparkles, ArrowRight, X, Moon } from 'lucide-react';

export default function MeditationPromoBar() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Reveal after slight scroll so it doesn't compete with the hero
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem('mtm_med_promo_dismissed') === '1') {
      setDismissed(true);
      return;
    }
    const onScroll = () => setVisible(window.scrollY > 400);
    onScroll();
    window.addEventListener('scroll', onScroll);

    // Auto-expand once after 2.5s for discoverability, then collapse
    const expandTimer = setTimeout(() => setExpanded(true), 2500);
    const collapseTimer = setTimeout(() => setExpanded(false), 6500);

    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(expandTimer);
      clearTimeout(collapseTimer);
    };
  }, []);

  const dismiss = (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      sessionStorage.setItem('mtm_med_promo_dismissed', '1');
    } catch (err) {}
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <>
      {/* Local styles for animations */}
      <style>{`
        @keyframes mtm-float {
          0%, 100% { transform: translateY(-50%) translateX(0); }
          50% { transform: translateY(-52%) translateX(-2px); }
        }
        @keyframes mtm-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes mtm-orbit {
          0% { transform: rotate(0deg) translateX(6px) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: rotate(360deg) translateX(6px) rotate(-360deg); opacity: 0; }
        }
        .mtm-float { animation: mtm-float 4s ease-in-out infinite; }
        .mtm-shimmer {
          background: linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.28) 50%, transparent 65%);
          background-size: 200% 100%;
          animation: mtm-shimmer 2.6s linear infinite;
        }
        .mtm-orbit { animation: mtm-orbit 3.2s linear infinite; }
      `}</style>

      {/* DESKTOP: Floating right-side pill */}
      <a
        href="/meditation-bundle"
        aria-label="Open Meditation Bundle landing page"
        className={`hidden md:flex fixed right-0 top-1/2 -translate-y-1/2 z-40 group items-center transition-all duration-500 ${
          visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8 pointer-events-none'
        }`}
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
      >
        <div
          className={`mtm-float relative flex items-center gap-3 pl-3 pr-4 py-3 rounded-l-2xl border border-r-0 border-amber-300/40 bg-gradient-to-r from-emerald-500/95 via-emerald-500/95 to-amber-400/90 text-slate-900 shadow-[0_10px_40px_-8px_rgba(16,185,129,0.55)] backdrop-blur-md overflow-hidden transition-[max-width] duration-500 ease-out ${
            expanded ? 'max-w-[320px]' : 'max-w-[62px]'
          }`}
          style={{ minHeight: '62px' }}
        >
          {/* Shimmer sweep */}
          <span className="absolute inset-0 mtm-shimmer pointer-events-none" />

          {/* Icon + orbiting sparkles */}
          <div className="relative flex-shrink-0 w-9 h-9 rounded-full bg-slate-900/90 flex items-center justify-center border border-white/20">
            <Moon size={18} className="text-amber-300" />
            <Sparkles
              size={10}
              className="mtm-orbit absolute text-amber-300"
              style={{ top: '50%', left: '50%', marginTop: -5, marginLeft: -5 }}
            />
          </div>

          <div
            className={`relative flex flex-col leading-tight transition-opacity duration-300 whitespace-nowrap ${
              expanded ? 'opacity-100 delay-150' : 'opacity-0'
            }`}
          >
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-800">
              New &middot; Limited Offer
            </span>
            <span className="text-sm font-extrabold">Supreme Awakening Bundle @ $39 USD</span>
          </div>

          <ArrowRight
            size={16}
            className={`relative flex-shrink-0 transition-all duration-300 ${
              expanded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
            } group-hover:translate-x-1`}
          />

          {/* Dismiss */}
          <button
            onClick={dismiss}
            aria-label="Dismiss meditation promo"
            className={`relative flex-shrink-0 -mr-1 w-5 h-5 rounded-full bg-slate-900/25 hover:bg-slate-900/45 text-slate-900 flex items-center justify-center transition-opacity ${
              expanded ? 'opacity-90' : 'opacity-0 pointer-events-none'
            }`}
          >
            <X size={11} />
          </button>

          {/* Outer glow */}
          <span className="absolute inset-0 rounded-l-2xl pointer-events-none shadow-[inset_0_0_0_1px_rgba(255,255,255,0.22)]" />
        </div>
      </a>

      {/* MOBILE: Slim top ticker banner */}
      <a
        href="/meditation-bundle"
        aria-label="Open Meditation Bundle landing page"
        className={`md:hidden fixed left-3 right-3 top-[68px] z-40 rounded-full overflow-hidden border border-amber-300/40 shadow-[0_10px_25px_-10px_rgba(16,185,129,0.6)] transition-all duration-500 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3 pointer-events-none'
        }`}
      >
        <div className="relative flex items-center gap-2.5 px-3.5 py-2 bg-gradient-to-r from-emerald-500 via-emerald-400 to-amber-400 text-slate-900">
          <span className="absolute inset-0 mtm-shimmer pointer-events-none" />
          <div className="relative flex-shrink-0 w-7 h-7 rounded-full bg-slate-900/90 flex items-center justify-center border border-white/20">
            <Moon size={14} className="text-amber-300" />
          </div>
          <div className="relative flex-1 min-w-0 leading-tight">
            <div className="text-[9px] font-bold uppercase tracking-widest text-slate-800">New</div>
            <div className="text-xs font-extrabold truncate">Supreme Awakening Bundle @ $39 USD</div>
          </div>
          <ArrowRight size={14} className="relative flex-shrink-0" />
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            className="relative flex-shrink-0 w-6 h-6 rounded-full bg-slate-900/25 flex items-center justify-center"
          >
            <X size={11} />
          </button>
        </div>
      </a>
    </>
  );
}
